// Rejestr plansz (zadan): tor, warunki proby i garaz (robot startowy) dla kazdej.
// Parametry trzymamy jako dane, nie jako kod - nowa plansza to nowy wpis, bez
// zmian w silniku fizyki (zgodnie z hipoteza H4 z briefu).

import type { PartLimits } from '../domain/limits';
import { EXTRA_CHALLENGES, EXTRA_REFERENCES } from './tracks';

/** Archetyp robota, ktorym da sie przejsc dana trase (uzywane w testach/podgladach). */
export type ReferenceRobot = 'good' | 'small' | 'huge' | 'fast';

export interface StaticBox {
  /** Srodek pudelka w metrach. */
  x: number;
  y: number;
  /** Pelna szerokosc i wysokosc w metrach. */
  w: number;
  h: number;
}

/**
 * Dynamiczna przeszkoda: skrzynka do przepchniecia albo kladka, po ktorej
 * sie jedzie. W odroznieniu od staticBoxes te bryly poruszaja sie w probie.
 */
export interface DynamicProp {
  id: string;
  /** Srodek (pozycja startowa) w metrach. */
  x: number;
  y: number;
  w: number;
  h: number;
  massKg: number;
  friction?: number;
  /** Przegub do swiata wzgledem srodka prop - dla ruchomej kladki (huśtawka). */
  pivot?: { x: number; y: number };
}

/** Warunek na przeszkode: musi przekroczyc podana linie X, by zaliczyc probe. */
export interface PropTarget {
  propId: string;
  minX: number;
}

export interface ChallengeDefinition {
  id: string;
  label: string;
  /** Krotka podpowiedz dla gracza - co trzeba zbudowac, by przejsc plansze. */
  hint: string;
  /** Limit kosztu konstrukcji na tej planszy (jednostki budzetu). */
  budgetPln: number;
  /**
   * Limity klockow na tej planszy (opcjonalne) - np. "na tej trasie najwyzej
   * dwa kola". Patrz `PartLimits` w domain/limits.ts.
   */
  limits?: PartLimits;
  /** Czas "mistrza" do pobicia (sekundy). Bez ducha - gracz nie widzi konstrukcji. */
  parTimeSec: number;
  staticBoxes: StaticBox[];
  /** Dynamiczne przeszkody (skrzynki, kladki) - poruszaja sie w probie. */
  dynamicProps?: DynamicProp[];
  /** Warunki na przeszkody (np. skrzynka musi dotrzec na mete). */
  propTargets?: PropTarget[];
  groundFriction: number;
  /** Wysokosc gornej powierzchni gruntu (m) - do posadowienia robota na starcie. */
  groundTopY: number;
  /** Lewa krawedz robota na starcie (m). */
  startX: number;
  /** Linia mety: cala konstrukcja musi ja przekroczyc. */
  finishX: number;
  /** Wysokosc gornej powierzchni platformy (m) - do sprawdzenia osiadania. */
  platformTopY: number;
  timeLimitSec: number;
  settleTimeSec: number;
  /** null wylacza skrot "zatrzymany przy przeszkodzie" (przydatne w testach). */
  stuckTimeoutSec: number | null;
  outOfBounds: { minX: number; maxX: number; minY: number; maxY: number };
  /** Ramka widoku dla kamery: zakres toru widoczny na ekranie (metry). */
  view: { minX: number; maxX: number; centerY: number };
}

const STEP_HEIGHT_M = 0.14;
const PLATFORM_TOP_Y = STEP_HEIGHT_M;

/** Plansza 1: prosty stopien 0,14 m - wystarcza duze kola. */
export const STEP_CHALLENGE: ChallengeDefinition = {
  id: 'step-v1',
  label: 'Stopien 0,14 m',
  hint: 'Niski stopien. Wystarcza duze kola (promien 0,25 m).',
  budgetPln: 45,
  parTimeSec: 5.0,
  staticBoxes: [
    // Grunt: gorna powierzchnia na y = 0, na lewo od stopnia.
    { x: -5, y: -0.5, w: 10, h: 1 },
    // Stopien + platforma: gorna powierzchnia na y = 0,14, na prawo od x = 0.
    { x: 5, y: PLATFORM_TOP_Y - 0.5, w: 10, h: 1 },
  ],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 3.5,
  platformTopY: PLATFORM_TOP_Y,
  timeLimitSec: 20,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.0,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 5.9, centerY: 0.7 },
};

/**
 * Plansza 2: krawedz wyzsza niz promien duzego kola (0,25 m).
 * Zeby ja pokonac, trzeba wiekszego kola ORAZ wiekszego momentu - czyli
 * kola ogromnego (r 0,375 m) i mocnego napedu.
 */
const LEDGE_HEIGHT_M = 0.26;

export const LEDGE_CHALLENGE: ChallengeDefinition = {
  id: 'ledge-v1',
  label: 'Wysoka krawedz 0,26 m',
  hint: 'Krawedz wyzej niz promien duzego kola - trzeba kola ogromnego i mocnego napedu.',
  budgetPln: 70,
  // Ostatnia trasa dokłada ograniczenie sprzetu: wolno uzyc najwyzej DWOCH kol
  // (np. { wheel: 1 } zostawiloby tylko jedno kolo). Reszta tras nie ma limitow.
  limits: { categories: { wheel: 2 } },
  parTimeSec: 6.0,
  staticBoxes: [
    { x: -5, y: -0.5, w: 10, h: 1 },
    { x: 5, y: LEDGE_HEIGHT_M - 0.5, w: 10, h: 1 },
  ],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 3.5,
  platformTopY: LEDGE_HEIGHT_M,
  timeLimitSec: 20,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.0,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 5.9, centerY: 0.85 },
};

/**
 * Plansza 3: szczelina 0,6 m. Kolo duze (srednica 0,5 m) wpada do srodka,
 * a kolo ogromne (srednica 0,75 m) przejezdza po obu krawedziach - to nauka
 * o tym, ze wieksze kolo wiecej "przestaje".
 */
const GAP_WIDTH_M = 0.6;
const GAP_TO_X = GAP_WIDTH_M;

export const GAP_CHALLENGE: ChallengeDefinition = {
  id: 'gap-v1',
  label: 'Szczelina 0,6 m',
  hint: 'Przerwa w podlozu. Przejezdza ja tylko kolo ogromne - male i duze wpadaja.',
  budgetPln: 70,
  parTimeSec: 5.5,
  staticBoxes: [
    { x: -5, y: -0.5, w: 10, h: 1 },
    { x: (GAP_TO_X + 10.4) / 2, y: -0.5, w: 10.4 - GAP_TO_X, h: 1 },
  ],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 3.0,
  platformTopY: 0,
  timeLimitSec: 20,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.0,
  // Wpadniecie do szczeliny to wypad poza obszar (na dole nie ma podloza).
  outOfBounds: { minX: -12, maxX: 14, minY: -1.2, maxY: 6 },
  view: { minX: -3.7, maxX: 5.9, centerY: 0.6 },
};


/** Plansza 4: ostry grzbiet (garb) wysoki na 0,14 m - wjazd i zjazd. */
const RIDGE_HEIGHT_M = 0.14;

export const RIDGE_CHALLENGE: ChallengeDefinition = {
  id: 'ridge-v1',
  label: 'Ostry grzbiet 0,14 m',
  hint: 'Garb w podlozu. Trzeba wjechac i zjechac - uwaga na srodek masy.',
  budgetPln: 45,
  parTimeSec: 5.0,
  staticBoxes: [
    { x: -5, y: -0.5, w: 10, h: 1 },
    { x: 0.5, y: RIDGE_HEIGHT_M - 0.5, w: 1, h: 1 },
    { x: 5.5, y: -0.5, w: 9, h: 1 },
  ],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 2.8,
  platformTopY: 0,
  timeLimitSec: 20,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.0,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 5.9, centerY: 0.6 },
};

/** Plansza 5: schody - dwa stopnie po 0,14 m (lacznie 0,28 m). */
const STAIR1_TOP_M = 0.14;
const STAIR2_TOP_M = 0.28;

export const STAIRS_CHALLENGE: ChallengeDefinition = {
  id: 'stairs-v1',
  label: 'Schody 0,14 + 0,14 m',
  hint: 'Dwa stopnie pod rzad. Kazdy osobno niski, ale razem wymagaja zapasu momentu.',
  budgetPln: 55,
  parTimeSec: 6.0,
  staticBoxes: [
    { x: -5, y: -0.5, w: 10, h: 1 },
    { x: 0.75, y: STAIR1_TOP_M - 0.5, w: 1.5, h: 1 },
    { x: 5.75, y: STAIR2_TOP_M - 0.5, w: 8.5, h: 1 },
  ],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 4.5,
  platformTopY: STAIR2_TOP_M,
  timeLimitSec: 22,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.0,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 6.4, centerY: 0.9 },
};

/** Plansza 6: dolina - zjazd w dol (0,14 m) i wyjazd z powrotem na gore. */
const VALLEY_DEPTH_M = 0.14;

export const VALLEY_CHALLENGE: ChallengeDefinition = {
  id: 'valley-v1',
  label: 'Dolina 0,14 m',
  hint: 'Najpierw zjazd w dol, potem wyjazd na gore. Robot rozpedza sie i musi wrocic.',
  budgetPln: 50,
  parTimeSec: 5.5,
  staticBoxes: [
    { x: -5, y: -0.5, w: 10, h: 1 },
    { x: 1.5, y: -VALLEY_DEPTH_M - 0.5, w: 3, h: 1 },
    { x: 6.5, y: -0.5, w: 7, h: 1 },
  ],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 5.0,
  platformTopY: 0,
  timeLimitSec: 22,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.0,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 6.9, centerY: 0.6 },
};

/**
 * Plansza: przewiezienie luznej skrzynki. Lekka skrzynka musi dotrzec za mete -
 * trzeba ja popychac (a nie przewrocic ani zgubic).
 */
export const CRATE_CHALLENGE: ChallengeDefinition = {
  id: 'crate-v1',
  label: 'Przewiezienie skrzynki',
  hint: 'Popchnij luzna skrzynke za mete. Niski lazik z malymi kolami popycha ja; duze kola wjezdzaja na nia i ja gubia.',
  budgetPln: 50,
  parTimeSec: 8.0,
  staticBoxes: [{ x: 0, y: -0.5, w: 24, h: 1 }],
  dynamicProps: [
    { id: 'crate', x: 0.6, y: 0.11, w: 0.5, h: 0.22, massKg: 1.2, friction: 0.4 },
  ],
  propTargets: [{ propId: 'crate', minX: 3.8 }],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 3.6,
  platformTopY: 0,
  timeLimitSec: 25,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 4.0,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 6.4, centerY: 0.6 },
};

/**
 * Plansza: ruchoma kladka. Przerwa bez podloza - trzeba przejechac po kladce,
 * ktora pod ciezarem moze sie przesunac.
 */
export const PLANK_CHALLENGE: ChallengeDefinition = {
  id: 'plank-v1',
  label: 'Ruchoma kladka',
  hint: 'Przerwa bez podloza. Przejedz po ruchomej kladce, ktora moze sie przesunac.',
  budgetPln: 55,
  parTimeSec: 6.5,
  staticBoxes: [
    { x: -5, y: -0.5, w: 10, h: 1 },
    { x: (0.7 + 10.4) / 2, y: -0.5, w: 10.4 - 0.7, h: 1 },
  ],
  dynamicProps: [{ id: 'plank', x: 0.35, y: 0.05, w: 1.1, h: 0.1, massKg: 2.5, friction: 0.95 }],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 3.0,
  platformTopY: 0,
  timeLimitSec: 22,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.0,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 5.9, centerY: 0.6 },
};

/**
 * Plansza: hustawka. Kladka na przegubie w srodku, zawieszona nad przepascia.
 * Robot wjezdza na nia ciezkim koncem, kladka sie przechyla i pozwala przejechac
 * na druga strone. Przegub trzyma kladke w miejscu - bez niego by odjechala.
 */
export const SEESAW_CHALLENGE: ChallengeDefinition = {
  id: 'seesaw-v1',
  label: 'Hustawka',
  hint: 'Kladka na przegubie nad przepascia. Wjedz na nia - przechyli sie i pozwoli przejsc.',
  budgetPln: 55,
  parTimeSec: 7.0,
  staticBoxes: [
    { x: -5, y: -0.5, w: 10, h: 1 },
    { x: (1.2 + 10.4) / 2, y: -0.5, w: 10.4 - 1.2, h: 1 },
  ],
  dynamicProps: [
    {
      id: 'seesaw',
      x: 0.6,
      y: 0.15,
      w: 1.8,
      h: 0.1,
      massKg: 3,
      friction: 0.9,
      pivot: { x: 0, y: 0 },
    },
  ],
  groundFriction: 0.85,
  groundTopY: 0,
  startX: -2.4,
  finishX: 3.0,
  platformTopY: 0,
  timeLimitSec: 22,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.0,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 5.9, centerY: 0.7 },
};

/**
 * Plansza: przepchniecie ciezarU. Ciezka skrzynka blokuje droge - trzeba ja
 * przepchnac mocnym napedem i dobra przyczepnoscia.
 */
export const PUSH_CHALLENGE: ChallengeDefinition = {
  id: 'push-v1',
  label: 'Przepchniecie ciezarU',
  hint: 'Ciezka skrzynka blokuje droge. Niski lazik z malymi kolami przepycha ja; wysokie kola sie na nia wspinaja.',
  budgetPln: 70,
  parTimeSec: 8.0,
  staticBoxes: [{ x: 0, y: -0.5, w: 24, h: 1 }],
  dynamicProps: [
    { id: 'weight', x: 0.8, y: 0.11, w: 0.8, h: 0.22, massKg: 4, friction: 0.5 },
  ],
  groundFriction: 0.9,
  groundTopY: 0,
  startX: -2.4,
  finishX: 3.5,
  platformTopY: 0,
  timeLimitSec: 25,
  settleTimeSec: 1.0,
  stuckTimeoutSec: 3.5,
  outOfBounds: { minX: -12, maxX: 14, minY: -2, maxY: 6 },
  view: { minX: -3.7, maxX: 5.9, centerY: 0.6 },
};

/** Plansze dostepne w grze, od najlatwiejszej. */
export const CHALLENGES: readonly ChallengeDefinition[] = [
  STEP_CHALLENGE,
  RIDGE_CHALLENGE,
  VALLEY_CHALLENGE,
  STAIRS_CHALLENGE,
  CRATE_CHALLENGE,
  GAP_CHALLENGE,
  PLANK_CHALLENGE,
  SEESAW_CHALLENGE,
  PUSH_CHALLENGE,
  LEDGE_CHALLENGE,
  ...EXTRA_CHALLENGES,
];

/**
 * Archetyp robota przechodzacego dana trase: 10 rekordowych plansz recznie,
 * reszta z `tracks.ts`. `samples.ts` zamienia to na gotowe konstrukcje
 * (`REFERENCE_SOLUTIONS`) - dowod, ze kazda trase da sie przejsc.
 */
const BUILT_IN_REFERENCES: Readonly<Record<string, ReferenceRobot>> = {
  'step-v1': 'good',
  'ridge-v1': 'good',
  'valley-v1': 'good',
  'stairs-v1': 'good',
  'crate-v1': 'small',
  'gap-v1': 'huge',
  'plank-v1': 'good',
  'seesaw-v1': 'good',
  'push-v1': 'small',
  'ledge-v1': 'huge',
};

export const CHALLENGE_REFERENCES: Readonly<Record<string, ReferenceRobot>> = {
  ...BUILT_IN_REFERENCES,
  ...EXTRA_REFERENCES,
};

export type TrialOutcome = 'running' | 'won' | 'timeout' | 'out_of_bounds' | 'flipped' | 'stuck';
